import React, { useEffect, useState } from "react";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, Resolver, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { TemplateAppendixService } from "./service";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { Button, Card, CardBody, Col, Form, Row } from "react-bootstrap";
import { ITemplateAppendix } from "../../shared/interfaces/templateAppendix/ITemplateAppendix";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, ContentState, convertFromHTML } from "draft-js";
import { stateToHTML } from "draft-js-export-html";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { useUnsavedChanges } from "../../shared/hooks/useUnsavedChanges";

const templateAppendixSchema = yup.object().shape({
  name: yup.string().required("validation.required"),
  appendix: yup.string(),
});
const TemplateAppendixForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<ITemplateAppendix>({
    defaultValues: {
      name: "",
      appendix: "",
    },
    resolver: yupResolver(
      templateAppendixSchema,
    ) as unknown as Resolver<ITemplateAppendix>,
  });

  const { guardedNavigate, unsavedChangesModal } = useUnsavedChanges(isDirty);

  const onSubmit = (templateAppendix: ITemplateAppendix) => {
    const contentState = editorState.getCurrentContent();
    templateAppendix.appendix = stateToHTML(contentState);
    const action = isEdit
      ? TemplateAppendixService.update(templateAppendix, Number(id))
      : TemplateAppendixService.create(templateAppendix);

    action
      .then(() => {
        SuccessToast(
          t(`templateAppendix.${isEdit ? "successUpdate" : "successCreate"}`),
        );
        navigate("/template-appendix");
      })
      .catch(ErrorToast);
  };
  useEffect(() => {
    if (id) {
      const fetchTemplateAppendix = async () => {
        try {
          const response = await TemplateAppendixService.getSingle(+id!);
          reset(response);
          const blocksFromHTML = convertFromHTML(response?.appendix || "");
          const contentState = ContentState.createFromBlockArray(
            blocksFromHTML.contentBlocks,
            blocksFromHTML.entityMap,
          );
          setEditorState(EditorState.createWithContent(contentState));
        } catch (error) {
          ErrorToast(error);
        }
      };
      fetchTemplateAppendix();
    }
  }, [id]);
  return (
    <Card>
      <CardBody>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row className="g-3">
            <Col sm={12} xl={5}>
              <Form.Group>
                <Form.Label className={"required"}>
                  {t("general.name")} / {t("general.company")}
                </Form.Label>
                <Controller
                  name="name"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Form.Control
                      type="text"
                      isInvalid={!!errors?.name?.message?.length}
                      placeholder={t("general.enterName")}
                      onChange={onChange}
                      value={value}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name && t("validation.required")}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group className="mb-3" controlId="appendix">
                <Form.Label>{t("templateAppendix.appendix")}</Form.Label>
                <Controller
                  name="appendix"
                  control={control}
                  render={({ field }) => (
                    <Editor
                      editorState={editorState}
                      onEditorStateChange={(editorState) => {
                        setEditorState(editorState);
                        field.onChange(
                          stateToHTML(editorState.getCurrentContent()),
                        );
                      }}
                      wrapperClassName="demo-wrapper"
                      editorClassName={`form-control ${errors.appendix ? "is-invalid" : ""}`}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {t(errors.appendix?.message as string)}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col sm={12} className="d-flex gap-2">
              <Button type="submit" variant="primary">
                {t(`templateAppendix.${isEdit ? "update" : "create"}`)}
              </Button>
              <Button type="button" variant="secondary" onClick={() => guardedNavigate("/template-appendix")}>
                {t("button.cancel")}
              </Button>
            </Col>
          </Row>
        </Form>
        {unsavedChangesModal}
      </CardBody>
    </Card>
  );
};

export default TemplateAppendixForm;
